/**
 * WacklerLens — "View as Wackler" toggle state.
 *
 * Allows Commander to preview the portal through the external vendor's eyes.
 * When active, reconciliation screens redact internal-only detail (KDP codes,
 * undercharges, internal comments, intern-labelled sections) so what remains
 * mirrors the InvoiceResponse security contract.
 *
 * Persistence: URL query param `?lens=wackler` (copy-shareable) with a
 * localStorage fallback so a reload within the same tab keeps the lens state.
 * URL takes precedence on first load. Toggling updates both.
 */

import { useCallback, useEffect, useState } from 'react'

export const WACKLER_LENS_STORAGE_KEY = 'appic:wackler-lens'
export const WACKLER_LENS_URL_PARAM = 'lens'
export const WACKLER_LENS_URL_VALUE = 'wackler'
export const WACKLER_LENS_CHANGE_EVENT = 'appic:wackler-lens-change'

export function readWacklerLensFromUrl(): boolean | null {
  if (typeof window === 'undefined') return null
  try {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get(WACKLER_LENS_URL_PARAM)
    if (raw === null) return null
    return raw === WACKLER_LENS_URL_VALUE
  } catch {
    return null
  }
}

export function readWacklerLensFromStorage(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(WACKLER_LENS_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function writeStorage(active: boolean): void {
  if (typeof window === 'undefined') return
  try {
    if (active) window.localStorage.setItem(WACKLER_LENS_STORAGE_KEY, '1')
    else window.localStorage.removeItem(WACKLER_LENS_STORAGE_KEY)
  } catch {
    // Storage access denied (private mode) — URL is the SSoT in that case.
  }
}

function writeUrl(active: boolean): void {
  if (typeof window === 'undefined') return
  try {
    const url = new URL(window.location.href)
    if (active) url.searchParams.set(WACKLER_LENS_URL_PARAM, WACKLER_LENS_URL_VALUE)
    else url.searchParams.delete(WACKLER_LENS_URL_PARAM)
    window.history.replaceState(window.history.state, '', url.toString())
  } catch {
    // history.replaceState can throw in sandboxed iframes — storage still wins.
  }
}

export function resolveInitialWacklerLens(): boolean {
  const fromUrl = readWacklerLensFromUrl()
  if (fromUrl !== null) return fromUrl
  return readWacklerLensFromStorage()
}

export interface WacklerLens {
  active: boolean
  setActive: (value: boolean) => void
  toggle: () => void
}

/**
 * Reads and writes the WacklerLens across URL + localStorage. All call sites
 * share the same state through a lightweight custom event so Commander's
 * toggle click flips every visible screen at once without a provider.
 */
export function useWacklerLens(): WacklerLens {
  const [active, setActiveState] = useState<boolean>(resolveInitialWacklerLens)

  // Keep all mounted hook instances in sync. Window-level custom event is
  // cheap, doesn't require a provider, and survives across route splits.
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ active: boolean }>).detail
      if (typeof detail?.active === 'boolean') setActiveState(detail.active)
    }
    window.addEventListener(WACKLER_LENS_CHANGE_EVENT, onChange)
    return () => window.removeEventListener(WACKLER_LENS_CHANGE_EVENT, onChange)
  }, [])

  // Cross-tab + back/forward: rehydrate from URL if the browser changes it.
  useEffect(() => {
    const onPop = () => {
      const fromUrl = readWacklerLensFromUrl()
      if (fromUrl !== null) setActiveState(fromUrl)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const setActive = useCallback((value: boolean) => {
    writeStorage(value)
    writeUrl(value)
    setActiveState(value)
    window.dispatchEvent(new CustomEvent(WACKLER_LENS_CHANGE_EVENT, { detail: { active: value } }))
  }, [])

  const toggle = useCallback(() => {
    setActive(!active)
  }, [active, setActive])

  return { active, setActive, toggle }
}
